/** @jsx jsx */

import { css, jsx, SerializedStyles } from '@emotion/core';
import { Icon, Text, TooltipPosition } from '@zapier/design-system';
import { ReactNode } from 'react';
import { LoadingStatus, NoResultsStatus, UnableToLoadStatus } from './Status';
import { InfoIconTooltip } from 'app/developer-v3/components/InfoIconTooltip/InfoIconTooltip';

const styleTile = css`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 320px;
  padding: 20px;
`;
const styleHeader = css`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
`;
const styleInfo = css`
  display: flex;
  align-items: center;
  margin-bottom: 24px;
`;
const styleContent = css`
  display: flex;
  position: relative;
  flex-grow: 1;
  align-items: center;
  justify-content: center;
  justify-content: space-evenly;
`;

const styleDropdown = css`
  max-width: 100%;
  display: flex;
  justify-content: flex-end;
  flex: 1;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
`;

type BaseTileProps = Partial<{
  children: JSX.Element | JSX.Element[];
  icon: ReactNode;
  headerContent: ReactNode;
  style?: React.CSSProperties;
  tipContent: ReactNode;
  toolTipContent: ReactNode;
  tipContentStyle?: React.CSSProperties;
  tooltipPosition?: TooltipPosition;
  dropdowns: ReactNode[];
  dropdownContainerStyle?: SerializedStyles;
  dataTestId?: string;
}> &
  BaseTileState;

export type BaseTileState = Partial<{
  isLoading: boolean;
  isError: boolean;
  isNoData?: boolean;
}>;

export const BaseTile = ({
  icon = <Icon name="zapierLogo" size="24px" />,
  isLoading = true,
  isError = false,
  isNoData = false,
  headerContent = 'title',
  style = undefined,
  tipContent = undefined,
  tipContentStyle = undefined,
  toolTipContent = undefined,
  tooltipPosition = 'southwest',
  dropdowns = undefined,
  dropdownContainerStyle = undefined,
  children = [],
  dataTestId = undefined,
}: BaseTileProps) => (
  <div css={styleTile} data-testid={dataTestId} style={style}>
    <div
      css={styleHeader}
      style={{ flexDirection: dropdowns?.length > 1 ? 'column' : 'row' }}
    >
      <div css={styleInfo}>
        {icon}
        <Text fontWeight={600} margin="6px" type="ParagraphHeader1">
          {headerContent}
        </Text>
        <InfoIconTooltip
          allowMultilineTooltip={true}
          content={toolTipContent}
          position={tooltipPosition}
          size="20px"
        />
      </div>
      {dropdowns && (
        <div css={[styleDropdown, dropdownContainerStyle]}>{dropdowns}</div>
      )}
    </div>
    <div css={styleContent}>
      {(() => {
        if (isLoading) {
          return <LoadingStatus />;
        } else if (isError) {
          return <UnableToLoadStatus />;
        } else if (isNoData) {
          return <NoResultsStatus />;
        } else {
          return children;
        }
      })()}
    </div>
    {tipContent && (
      <div style={tipContentStyle}>
        <Text tag="p" type="Body3">
          TIP: {}
          <Text type="Body1">{tipContent}</Text>
        </Text>
      </div>
    )}
  </div>
);

export default BaseTile;
